import api from './api';

export const purchaseOrderService = {
  // Récupérer toutes les commandes d'achat
  async getPurchaseOrders(params = {}) {
    try {
      const response = await api.get('/purchase-orders', { params });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes:', error);
      throw error;
    }
  },

  // Récupérer une commande par ID
  async getPurchaseOrderById(id) {
    try {
      const response = await api.get(`/purchase-orders/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération de la commande:', error);
      throw error;
    }
  },

  // Créer une nouvelle commande
  async createPurchaseOrder(data) {
    try {
      const response = await api.post('/purchase-orders', data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la commande:', error);
      throw error;
    }
  },

  // Mettre à jour une commande
  async updatePurchaseOrder(id, data) {
    try {
      const response = await api.put(`/purchase-orders/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la commande:', error);
      throw error;
    }
  },

  // Supprimer une commande
  async deletePurchaseOrder(id) {
    try {
      const response = await api.delete(`/purchase-orders/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression de la commande:', error);
      throw error;
    }
  },

  // 🔴 `updateOrderStatus` A ÉTÉ RETIRÉE avec la route qu'elle appelait.
  // Elle n'avait AUCUN appelant dans l'application, et elle n'aurait de toute
  // façon rien fait : elle envoyait un `PATCH` là où la route était un `PUT`
  // — un 405, pas une mise à jour. *Une méthode de service qui appelle une
  // route absente est une promesse creuse* : elle apparaît à l'autocomplétion,
  // se lit comme une capacité disponible, et n'échoue qu'à l'exécution. Même
  // famille que les deux routes fantômes du lot RU6.
  // Les transitions passent désormais par `markSent`, `sendPurchaseOrderEmail`,
  // `cancelOrder` et les reçus — chacune porte sa règle côté serveur.

  // Ajouter un article à une commande
  async addItemToOrder(orderId, itemData) {
    try {
      const response = await api.post(`/purchase-orders/${orderId}/items`, itemData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'ajout de l\'article:', error);
      throw error;
    }
  },

  // Supprimer un article d'une commande
  async removeItemFromOrder(orderId, itemId) {
    try {
      const response = await api.delete(`/purchase-orders/${orderId}/items/${itemId}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'article:', error);
      throw error;
    }
  },

  // Envoyer une commande par email
  async sendPurchaseOrderEmail(orderId) {
    try {
      const response = await api.post(`/purchase-orders/${orderId}/send-email`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la commande par email:', error);
      throw error;
    }
  },

  async cancelOrder(id) {
    try {
      const response = await api.post(`/purchase-orders/${id}/cancel`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'annulation de la commande:', error);
      throw error;
    }
  },

  // Marquer une commande comme envoyée sans envoi mail (découplé)
  async markSent(id) {
    try {
      const response = await api.post(`/purchase-orders/${id}/mark-sent`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du marquage comme envoyée:', error);
      throw error;
    }
  },

  // Créer des commandes depuis la sélection de stock
  async createOrdersFromStock(selectedProducts) {
    try {
      const response = await api.post('/purchase-orders/create-from-stock', {
        selected_products: selectedProducts
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création des commandes depuis le stock:', error);
      throw error;
    }
  },

  // Récupérer les statuts disponibles
  async getPurchaseOrderStatuses() {
    try {
      const response = await api.get('/purchase-orders/statuses');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statuts:', error);
      throw error;
    }
  },

  // === NOUVELLES MÉTHODES POUR LES RÉCEPTIONS ===

  // Récupérer les commandes pour la page de réception
  async getReceptionOrders(params = {}) {
    try {
      const response = await api.get('/purchase-orders/reception', { params });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes de réception:', error);
      throw error;
    }
  },

  // Récupérer les commandes pour la page de comptabilité
  async getAccountingOrders(params = {}) {
    try {
      const response = await api.get('/purchase-orders/accounting', { params });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes de comptabilité:', error);
      throw error;
    }
  },

  // Créer un reçu pour une commande
  async createReceipt(orderId, receiptData, isFormData = false) {
    try {
      let response;
      if (isFormData) {
        // Upload multipart : override du Content-Type par défaut (application/json) de
        // l'instance api, sinon axios sérialise le FormData en JSON (formDataToJSON).
        try {
          response = await api.post(`/purchase-orders/${orderId}/receipts`, receiptData, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch (err) {
          // Iso : conserver error.message = message serveur (comme l'ancien fetch).
          throw new Error(err.response?.data?.error || 'Erreur lors de la création du reçu');
        }
        return response.data;
      } else {
        response = await api.post(`/purchase-orders/${orderId}/receipts`, receiptData);
        return response.data;
      }
    } catch (error) {
      console.error('Erreur lors de la création du reçu:', error);
      throw error;
    }
  },

  // Récupérer les reçus d'une commande
  async getOrderReceipts(orderId) {
    try {
      const response = await api.get(`/purchase-orders/${orderId}/receipts`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des reçus:', error);
      throw error;
    }
  },

  // Récupérer un reçu spécifique
  async getOrderReceipt(orderId, receiptId) {
    try {
      const response = await api.get(`/purchase-orders/${orderId}/receipts/${receiptId}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération du reçu:', error);
      throw error;
    }
  },

  // Mettre à jour un reçu (principalement pour ajouter/modifier la pièce jointe)
  async updateReceiptAttachment(orderId, receiptId, attachmentFile, comment = null) {
    try {
      const formData = new FormData();
      if (attachmentFile) {
        formData.append('attachment', attachmentFile);
      }
      if (comment !== null) {
        formData.append('comment', comment);
      }

      let response;
      try {
        response = await api.put(`/purchase-orders/${orderId}/receipts/${receiptId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch (err) {
        throw new Error(err.response?.data?.error || 'Erreur lors de la mise à jour du reçu');
      }
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du reçu:', error);
      throw error;
    }
  },

  // Supprimer un reçu
  async deleteOrderReceipt(orderId, receiptId) {
    try {
      const response = await api.delete(`/purchase-orders/${orderId}/receipts/${receiptId}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression du reçu:', error);
      throw error;
    }
  },

  // === MÉTHODES POUR LES PAIEMENTS ===

  // ⚠️ `getAllPayments()` A ÉTÉ SUPPRIMÉE au lot RU4-b, et c'est CE LOT qui l'a
  // rendue orpheline : elle n'avait qu'un appelant, le tableau des commandes,
  // qui n'en a plus besoin depuis que la liste sert `total_paid` par ligne.
  // Un wrapper de service sans importateur est du code qu'on croit encore
  // appelé — même famille que `utils/dateUtils.js`, retiré et non remplacé.
  // 🟡 Sa route serveur, elle, EXISTE TOUJOURS et n'a plus aucun appelant côté
  // front : `backend/routes/purchase_orders.py:1091`, `GET /purchase-orders/payments`.
  // Elle n'est pas retirée ici — supprimer une route est une décision d'API,
  // pas un geste d'un lot d'interface. Consignée au ROADMAP avec son ancrage.

  // Récupérer les paiements d'une commande
  async getOrderPayments(orderId) {
    try {
      const response = await api.get(`/purchase-orders/${orderId}/payments`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des paiements:', error);
      throw error;
    }
  },

  // Créer un paiement pour une commande
  async createOrderPayment(orderId, paymentData, isFormData = false) {
    try {
      let response;
      if (isFormData) {
        // Upload multipart : override du Content-Type par défaut (application/json) de
        // l'instance api, sinon axios sérialise le FormData en JSON (formDataToJSON).
        try {
          response = await api.post(`/purchase-orders/${orderId}/payments`, paymentData, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch (err) {
          // Iso : conserver error.message = message serveur (comme l'ancien fetch).
          throw new Error(err.response?.data?.error || 'Erreur lors de la création du paiement');
        }
        return response.data;
      } else {
        response = await api.post(`/purchase-orders/${orderId}/payments`, paymentData);
        return response.data;
      }
    } catch (error) {
      console.error('Erreur lors de la création du paiement:', error);
      throw error;
    }
  },

  // Supprimer un paiement
  async deleteOrderPayment(orderId, paymentId) {
    try {
      const response = await api.delete(`/purchase-orders/${orderId}/payments/${paymentId}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression du paiement:', error);
      throw error;
    }
  }
}; 